import * as Sentry from '@sentry/nuxt'

Sentry.init({
    // If set up, you can use your runtime config here
    // dsn: useRuntimeConfig().public.sentry.dsn,
    dsn: 'https://16a70809620e3f271a535034db170000@o4509162164453376.ingest.de.sentry.io/4509162648174672',

    // Release is automatically injected by @sentry/nuxt bundler plugin
    // Configured in nuxt.config.ts > sentry.sourceMapsUploadOptions.release

    integrations: [
        // send console.log, console.warn, and console.error calls as logs to Sentry
        Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],

    // Enable logs to be sent to Sentry
    enableLogs: true,

    beforeSend(event) {
        const exception = event.exception?.values?.[0];

        // Filter out expected HTTP errors (404 - page not found)
        // These are expected behavior for old/removed URLs and should not pollute Sentry
        const statusCode = event.contexts?.response?.status_code;
        const exceptionMessage = exception?.value || '';
        const exceptionType = exception?.type || '';

        const isExpectedHttpError = (
            statusCode === 404 ||
            exceptionMessage.includes('404') ||
            exceptionMessage.includes('Page Not Found') ||
            exceptionType === 'NuxtError' && exceptionMessage.includes('404')
        );

        if (isExpectedHttpError) {
            return null;
        }

        // Filter out deploy-skew artefacts: old tab tries to load a chunk that no
        // longer exists on the new build. Handled by chunk-error-handler.client.ts
        // (auto-reload), so the Sentry events are noise.
        if (exception) {
            const message = exception.value || '';
            const mechanismType = exception.mechanism?.type || '';

            const isStaleChunkError = (
                // Chrome/Edge
                message.includes('Failed to fetch dynamically imported module') ||
                // Safari/WebKit
                message.includes('Importing a module script failed') ||
                // Firefox
                message.includes('error loading dynamically imported module')
            );

            // Old-build closures referenced from deferred callbacks after the
            // new bundle has taken over. Correlated 1:1 with deploys.
            const isStaleClosureCallback = (
                message === 'Variable has not been set' &&
                mechanismType.startsWith('auto.browser.browserapierrors')
            );

            // Instagram/Facebook in-app browsers inject their own navigation
            // tracking script that calls the WKWebView native bridge without
            // feature-detecting it. Thrown by Meta's injected code, not ours,
            // and caught by our global onerror. See A4G-PWA-6TT.
            const isInAppBrowserBridge = message.includes('window.webkit.messageHandlers');

            // Client-side networks (mostly CN) rewrite changeLanguage()'s PATCH /context
            // to POST, which 405s - external issue, not fixable by us. Scoped via the
            // `callSite` tag (set at the capture site) rather than the stacktrace, since
            // beforeSend sees the minified production bundle, not readable frame names.
            const isFromChangeLanguageCall = event.tags?.callSite === 'changeLanguage';
            const isMangledContextPatch = (
                message.includes('No route found for "POST') &&
                message.includes('/store-api/context') &&
                isFromChangeLanguageCall
            );

            // Browser couldn't deliver all ResizeObserver notifications within a single
            // animation frame. Confirmed benign by a spec author (doesn't break the
            // page) - https://stackoverflow.com/q/49384120. See A4G-PWA-6TJ.
            const isResizeObserverLoopLimit = (
                message.includes('ResizeObserver loop limit exceeded') ||
                message.includes('ResizeObserver loop completed with undelivered notifications')
            );

            if (isStaleChunkError || isStaleClosureCallback || isInAppBrowserBridge || isMangledContextPatch || isResizeObserverLoopLimit) {
                return null;
            }
        }

        // Filter out errors from external scripts
        if (exception?.stacktrace?.frames) {
            const frames = exception.stacktrace.frames;
            const hasExternalFrame = frames.some(frame => {
                const filename = frame.filename || '';
                return (
                    filename.includes('google') ||
                    filename.includes('facebook') ||
                    filename.includes('twitter') ||
                    filename.includes('cdn.') ||
                    filename.includes('unpkg') ||
                    filename.includes('jsdelivr') ||
                    filename.includes('sizekick') ||
                    filename.includes('scarabresearch') ||
                    filename.includes('usercentrics') ||
                    filename.includes('shoesize')
                );
            });

            if (hasExternalFrame) {
                return null; // Filter out
            }
        }

        return event;
    },

    // Reduce sample rate dramatically
    sampleRate: 0.2, // Only send 20% of errors
    // tracesSampleRate omitted - no performance monitoring, only error tracking

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,
});
